import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articlePreviews } from '@/mocks/articles';

const siteOrigin = import.meta.env.VITE_SITE_URL || 'https://basetaosheet.sbs';

function buildArticleSchema(article: typeof articlePreviews[0]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    url: `${siteOrigin}/article/${article.slug}`,
    datePublished: '2026-08-01',
    dateModified: '2026-08-10',
    author: {
      '@type': 'Organization',
      name: 'Basetao Spreadsheet Hub',
      url: siteOrigin,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Basetao Spreadsheet Hub',
      url: siteOrigin,
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/0dc5bc4081b05862da3d01553c9c9d93.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteOrigin}/article/${article.slug}`,
    },
  };
}

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = articlePreviews.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-24 pb-14 md:pb-20 px-6 md:px-10 bg-background-50 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-3">Article Not Found</h1>
            <p className="text-foreground-600 mb-6">The article you are looking for does not exist or has been moved.</p>
            <Link to="/" className="inline-flex items-center px-5 py-2.5 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-md transition-colors duration-200">
              Back to Homepage
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleSchema = buildArticleSchema(article);

  return (
    <>
      <Navbar />
      <main>
        <article className="pt-24 pb-14 md:pb-20 px-6 md:px-10 bg-background-50">
          <div className="max-w-[800px] mx-auto">
            <nav className="mb-6" aria-label="Breadcrumb">
              <ol className="flex items-center gap-2 text-sm text-foreground-500">
                <li>
                  <Link to="/" className="hover:text-primary-500 transition-colors duration-200">
                    Home
                  </Link>
                </li>
                <li><i className="ri-arrow-right-s-line text-xs"></i></li>
                <li className="text-foreground-700 font-medium">{article.title}</li>
              </ol>
            </nav>

            <header className="mb-10">
              <span className="inline-block px-3 py-1 text-xs font-semibold tracking-wider uppercase text-accent-700 bg-accent-100 rounded-full mb-4">
                {article.category}
              </span>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>
              <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
                {article.excerpt}
              </p>
              <div className="flex flex-wrap items-center gap-4 mt-5 text-xs text-foreground-500">
                <span>Updated: August 2026</span>
                <span>&middot;</span>
                <span>8 min read</span>
              </div>
            </header>

            <div className="mb-10">
              <img
                src={article.image}
                alt={article.title}
                title={article.title}
                className="w-full h-64 md:h-80 object-cover rounded-lg"
              />
            </div>

            <div className="prose-custom">
              <section className="mb-10">
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">Introduction</h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  Welcome to our in-depth guide on <strong>{article.title.toLowerCase()}</strong>. At BasetaoSheet, we are dedicated to providing the most comprehensive, up-to-date, and actionable information for smart shoppers who rely on the <strong>Basetao Spreadsheet</strong> system. Whether you are a first-time visitor exploring how the platform works or a seasoned user looking to refine your approach, this article covers everything you need.
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  The <strong>Basetao Spreadsheet</strong> has become an essential tool for thousands of international shoppers who want to discover quality products, compare prices intelligently, understand shipping logistics, and make informed purchasing decisions. This article delves deep into the topic to give you practical, actionable insights backed by community experience and thorough research.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">Key Information You Need To Know</h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  When exploring <strong>{article.title.toLowerCase()}</strong>, there are several critical factors that every shopper should understand. The <strong>Basetao Spreadsheet</strong> ecosystem provides structured resources that organize product data, pricing comparisons, quality control insights, and shipping logistics into an accessible format. This organized approach dramatically reduces the time needed for research compared to traditional browsing methods where you would need to visit dozens of individual product pages.
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  One of the most significant advantages of using the <strong>Basetao Spreadsheet</strong> is the ability to see multiple products, sellers, and price points side by side. This bird&apos;s-eye view makes it much easier to identify genuine value, spot price anomalies, and find the best deals available in your preferred product categories. Rather than relying on guesswork, you can make data-driven decisions that lead to better outcomes.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">How This Affects Your Shopping Experience</h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  Understanding <strong>{article.title.toLowerCase()}</strong> can fundamentally transform how you approach shopping through the platform. Many users report that after familiarizing themselves with the <strong>Basetao Spreadsheet</strong> system, they save significant amounts of both time and money. The key is knowing not just what information is available, but how to interpret and act on it effectively.
                </p>
                <div className="bg-secondary-50 rounded-lg p-5 md:p-6 mb-4 space-y-3">
                  <div className="flex gap-3">
                    <span className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs font-bold">1</span>
                    <div>
                      <strong className="text-foreground-800 text-sm">Research First</strong>
                      <p className="text-sm text-foreground-600 mt-1">Always spend time reviewing the spreadsheet data before committing to a purchase. Compare at least three options for any significant order to ensure you are getting the best value.</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <span className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs font-bold">2</span>
                    <div>
                      <strong className="text-foreground-800 text-sm">Verify Independently</strong>
                      <p className="text-sm text-foreground-600 mt-1">Cross-reference spreadsheet data with seller reviews, community feedback, and recent buyer experiences to build a complete picture before ordering.</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <span className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs font-bold">3</span>
                    <div>
                      <strong className="text-foreground-800 text-sm">Plan Your Logistics</strong>
                      <p className="text-sm text-foreground-600 mt-1">Factor in shipping costs, warehouse timelines, and potential customs duties when calculating the total cost of your order.</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <span className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs font-bold">4</span>
                    <div>
                      <strong className="text-foreground-800 text-sm">Start Small, Scale Up</strong>
                      <p className="text-sm text-foreground-600 mt-1">Begin with modest orders to build familiarity with the platform and specific sellers before scaling up to larger purchases.</p>
                    </div>
                  </div>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">Practical Tips For Success</h2>
                <ul className="space-y-3 mb-4">
                  <li className="flex gap-3 text-sm text-foreground-700">
                    <i className="ri-check-line text-accent-600 mt-0.5 flex-shrink-0"></i>
                    <span><strong>Stay Updated:</strong> The <strong>Basetao Spreadsheet</strong> is regularly refreshed with new product listings, updated pricing, and community feedback. Check back frequently to catch the latest additions and deals before they are widely discovered.</span>
                  </li>
                  <li className="flex gap-3 text-sm text-foreground-700">
                    <i className="ri-check-line text-accent-600 mt-0.5 flex-shrink-0"></i>
                    <span><strong>Build Seller Relationships:</strong> Over time, you will identify sellers who consistently deliver quality products. Building a shortlist of trusted sellers can dramatically improve your shopping efficiency and reduce the risk of disappointing purchases.</span>
                  </li>
                  <li className="flex gap-3 text-sm text-foreground-700">
                    <i className="ri-check-line text-accent-600 mt-0.5 flex-shrink-0"></i>
                    <span><strong>Leverage QC Resources:</strong> Quality control is your safety net. Always take time to review QC photos thoroughly and do not hesitate to request additional images if anything looks unclear or suspicious.</span>
                  </li>
                  <li className="flex gap-3 text-sm text-foreground-700">
                    <i className="ri-check-line text-accent-600 mt-0.5 flex-shrink-0"></i>
                    <span><strong>Compare Shipping Options:</strong> Different shipping lines offer varying balances of speed, cost, and reliability. The spreadsheet format makes it easy to compare these options and select the one that best fits your priorities.</span>
                  </li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">Common Questions Answered</h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  We frequently receive questions from the community about <strong>{article.title.toLowerCase()}</strong>. Here are the most important answers based on our research and community feedback:
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  The <strong>Basetao Spreadsheet</strong> system is designed to be accessible to users of all experience levels. Beginners will find the structured format intuitive and easy to navigate, while advanced users can leverage the detailed data fields for deeper analysis and more strategic purchasing decisions. The platform continues to evolve with community input, ensuring it stays relevant and useful for everyone.
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  For any additional questions about <strong>Basetao Spreadsheet</strong> that are not covered in this article, we encourage you to explore our other comprehensive guides on the homepage or reach out to the community for real-time advice and shared experiences. The collective knowledge of experienced Basetao Spreadsheet users is one of the most valuable resources available to new shoppers.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">Final Verdict &amp; Next Steps</h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  After thoroughly exploring <strong>{article.title.toLowerCase()}</strong>, we can confidently say that the <strong>Basetao Spreadsheet</strong> ecosystem provides genuine value for shoppers who want to make informed, data-driven purchasing decisions. The combination of organized product data, community insights, and accessible quality control resources creates a powerful toolkit that can save significant time and money.
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  If you are new to the platform, we recommend starting with our <Link to="/basetao-spreadsheet-guide-2026" className="text-primary-600 hover:text-primary-700 font-medium underline">Ultimate Basetao Spreadsheet Guide 2026</Link> for a comprehensive introduction. For experienced users looking to deepen their knowledge, browse our other <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium underline">in-depth articles and resources</Link> covering shipping, warehouse management, safety, and more.
                </p>
              </section>

              <div className="bg-primary-50 border border-primary-200 rounded-lg p-6 md:p-8 text-center mb-10">
                <h3 className="font-heading text-xl font-bold text-foreground-900 mb-3">
                  Ready to Explore Basetao Spreadsheet?
                </h3>
                <p className="text-sm text-foreground-600 mb-5 max-w-lg mx-auto">
                  Visit the main website to browse products, compare prices, and start shopping smarter with organized spreadsheet data.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center px-6 py-3 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-md transition-colors duration-200 cursor-pointer whitespace-nowrap"
                  >
                    Basetao Spreadsheet
                    <i className="ri-arrow-right-up-line ml-2"></i>
                  </a>
                  <Link
                    to="/"
                    className="inline-flex items-center px-6 py-3 text-sm font-semibold text-primary-600 border border-primary-300 hover:bg-primary-50 rounded-md transition-colors duration-200 cursor-pointer whitespace-nowrap"
                  >
                    Back to Homepage
                  </Link>
                </div>
              </div>

              <nav className="border-t border-secondary-200/60 pt-6 flex flex-wrap items-center justify-between gap-4">
                <Link
                  to="/"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground-600 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  Back to Basetao Spreadsheet Home
                </Link>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors duration-200 whitespace-nowrap"
                >
                  Visit Main Website
                  <i className="ri-arrow-right-up-line"></i>
                </a>
              </nav>
            </div>
          </div>
        </article>
      </main>
      <Footer />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
    </>
  );
}